import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, ROLE_LABELS, rolesOf } from "@/context/AuthContext";
import { navForRoles } from "@/lib/nav";
import { toast } from "sonner";
import { Users, Plus, Trash2, Pencil } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", roles: ["worker"] };

export default function Team() {
  const { user } = useAuth();
  const isAdmin = rolesOf(user).includes("admin");
  const [items, setItems] = useState([]);
  const [roleOpts, setRoleOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editUser, setEditUser] = useState(null);
  const [editRoles, setEditRoles] = useState([]);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/roles").then((r) => setRoleOpts(r.data || [])).catch(() => {});
  }, []);

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const create = async () => {
    try {
      await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, roles: form.roles });
      toast.success("Team member added");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (u) => { setEditUser(u); setEditRoles(rolesOf(u)); setEditMenus(Array.isArray(u.allowed_menus) && u.allowed_menus.length ? u.allowed_menus : null); };
  const saveRoles = async () => {
    try {
      await api.patch(`/users/${editUser.id}/roles`, { roles: editRoles });
      await api.patch(`/users/${editUser.id}/menus`, { menus: editMenus });
      toast.success("Access updated"); setEditUser(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [editMenus, setEditMenus] = useState(null);   // null = all menus allowed by roles
  const menuOpts = navForRoles(editRoles);
  const toggleMenu = (to) => {
    const cur = editMenus || menuOpts.map((n) => n.to);
    setEditMenus(cur.includes(to) ? cur.filter((x) => x !== to) : [...cur, to]);
  };
  const remove = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const RoleGrid = ({ selected, onToggle, testprefix }) => (
    <div className="grid grid-cols-2 gap-2">
      {roleOpts.map((r) => (
        <label key={r.value} data-testid={`${testprefix}-${r.value}`} className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs hover:border-primary/40">
          <Checkbox checked={selected.includes(r.value)} onCheckedChange={() => onToggle(r.value)} />
          <span>{r.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      <PageHeader title="Team & Roles" subtitle="Manage staff accounts. One person can hold several roles." icon={Users}
        action={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> Add Member</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add Team Member</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Name</Label>
                  <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email</Label>
                    <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@aranyaexim.com" /></div>
                  <div className="space-y-2"><Label>Password</Label>
                    <Input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" /></div>
                </div>
                <div className="space-y-2"><Label>Roles (pick one or more)</Label>
                  <RoleGrid selected={form.roles} onToggle={(v) => toggle(form.roles, (r) => setForm({ ...form, roles: r }), v)} testprefix="user-role" />
                </div>
              </div>
              <DialogFooter><Button data-testid="user-save" onClick={create} disabled={!form.name || !form.email || !form.password || form.roles.length === 0}>Add Member</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      {items.length === 0 ? (
        <EmptyState icon={Users} title="No team members" hint="Add staff and assign their roles." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Roles</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="transition-colors duration-200 hover:bg-muted/40">
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {rolesOf(u).map((r) => (
                        <Badge key={r} variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-[11px] font-semibold text-primary">{ROLE_LABELS[r] || r}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {isAdmin && (
                      <span className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="h-8 w-8 text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></Button>
                        {u.id !== user.id && (
                          <Button variant="ghost" size="icon" data-testid={`user-delete-${u.id}`} onClick={() => remove(u.id)} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        )}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit access — {editUser?.name}</DialogTitle></DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Roles</p>
          <RoleGrid selected={editRoles} onToggle={(v) => toggle(editRoles, setEditRoles, v)} testprefix="edit-role" />
          {!editRoles.includes("admin") && (
            <div className="space-y-2 border-t border-border pt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Menus this person can see</p>
                <button type="button" data-testid="edit-menus-reset" className="text-xs text-primary hover:underline" onClick={() => setEditMenus(null)}>All (default)</button>
              </div>
              <div className="grid max-h-56 grid-cols-2 gap-1.5 overflow-y-auto pr-1">
                {menuOpts.map((n) => {
                  const on = !editMenus || editMenus.includes(n.to);
                  return (
                    <label key={n.to} data-testid={`edit-menu-${n.to.replace("/", "") || "dashboard"}`} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${on ? "border-primary/40 bg-primary/5" : "border-border opacity-70"}`}>
                      <Checkbox checked={on} onCheckedChange={() => toggleMenu(n.to)} />
                      <n.icon className="h-3.5 w-3.5 text-muted-foreground" /> {n.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="edit-roles-save" onClick={saveRoles} disabled={editRoles.length === 0 || (editMenus && editMenus.length === 0)}>Save Access</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
